import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Dropdown, Menu } from 'antd';
import { InstallmentDetails } from '~/components/Payments/InstallmentDetails/InstallmentDetails';
import { PayOverdueInstallments } from '~/components/Payments/PayOverdueInstallments/PayOverdueInstallments';
import { useDrawer } from '~/hooks/useDrawer';
import { Installment } from '~/typings/entities/Payments';
import { ManagedStatusNames, PaymentStatusColors, PaymentStatusNames } from '~/typings/enums/Payment';
import { Button, CellProps, Table, Tag } from '~/ui/components';
import { cellToDate, cellToMoney } from '~/common/utils/table';

type FlatInstallments = Omit<Installment, 'installments'> & {
  ccb: string;
  product?: string;
  contractId?: number;
  canPayOverdue?: boolean;
};

const renderLink = (props: CellProps<FlatInstallments>, document: string) => {
  const value = props?.getValue<number | string>();
  if (props.row.getIsGrouped()) return value;
  return (
    <Link
      target="_blank"
      to={`/backoffice/contracts?ccbNumber=${value}&computeCommissioning=false&document=${document}`}
    >
      {value}
    </Link>
  );
};

const renderStatus = (props: CellProps<FlatInstallments>) => {
  const status = props?.getValue<string>();
  const colors = PaymentStatusColors?.[status] || PaymentStatusColors.DEFAULT;
  return <Tag {...colors}>{PaymentStatusNames?.[status] || status}</Tag>;
};

const renderManagedStatus = (props: CellProps<FlatInstallments>) => {
  const managedStatus = props?.getValue<string>();
  return PaymentStatusNames?.[managedStatus] || ManagedStatusNames?.[managedStatus] || managedStatus;
};

export type InstallmentsTableProps = {
  loading?: boolean;
  data: any[];
  hiddenColumns?: string[];
  document?: string;
  showPayOverdue?: boolean;
  contractId?: string;
  ccb?: string;
  personName?: string;
  personId?: number;
};

export function InstallmentsTable({
  loading,
  data,
  hiddenColumns,
  document,
  showPayOverdue,
  contractId,
  ccb,
  personName,
  personId,
}: InstallmentsTableProps) {
  const { openDrawer, closeAllDrawers } = useDrawer();

  const handleOpenPayOverdue = useCallback(
    (installment?: FlatInstallments) => {
      closeAllDrawers();
      const rowContractId = installment?.contractId ? String(installment.contractId) : contractId;
      openDrawer(
        'pay-overdue-installments',
        <PayOverdueInstallments
          document={document}
          contractId={rowContractId}
          personName={personName}
          personId={personId}
        />,
        { title: 'Pagar parcelas em atraso', width: 600, closable: true, maskClosable: true },
      );
    },
    [document, contractId, personName, personId],
  );

  const handleOpenDetails = useCallback(
    (installment: FlatInstallments) => {
      const extraAction =
        installment?.product === 'PRIVATE_CONSIGNED' && installment?.canPayOverdue !== false ? (
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key="pay" onClick={() => handleOpenPayOverdue(installment)}>
                  Pagar parcela
                </Menu.Item>
              </Menu>
            }
            trigger={['click']}
            placement="bottomRight"
          >
            <Button iconOnly icon="DotsVertical" variant="text" />
          </Dropdown>
        ) : undefined;

      openDrawer('installment-details', <InstallmentDetails installment={installment} />, {
        title: `Detalhe da parcela ${installment.installmentNumber}`,
        width: 557,
        closable: true,
        maskClosable: true,
        extraAction,
      });
    },
    [handleOpenPayOverdue, showPayOverdue, contractId, ccb],
  );

  function cellToActions(props: CellProps<FlatInstallments>) {
    if (props.row.getIsGrouped()) return <></>;
    const installment = props.row.original;

    const menu = (
      <Menu>
        {props.row.original?.product === 'PRIVATE_CONSIGNED' && props.row.original?.canPayOverdue !== false && (
          <Menu.Item key="pay" onClick={() => handleOpenPayOverdue(installment)}>
            Pagar parcela
          </Menu.Item>
        )}
        <Menu.Item key="details" onClick={() => handleOpenDetails(installment)}>
          Ver detalhe da parcela
        </Menu.Item>
      </Menu>
    );

    return (
      <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
        <Button iconOnly icon="DotsVertical" variant="text" />
      </Dropdown>
    );
  }

  const columns = useMemo<any>(() => {
    const initialColumns = [
      {
        accessorKey: 'ccb',
        header: () => 'CCB',
        cell: (cellProps: any) => renderLink(cellProps, document),
      },
      {
        accessorKey: 'installmentNumber',
        header: () => 'PARCELA',
        headerAlign: 'center',
        cellAlign: 'center',
        enableGrouping: false,
      },
      {
        accessorKey: 'dueDate',
        header: () => 'VENCIMENTO',
        cell: cellToDate('DD/MM/YYYY'),
        headerAlign: 'center',
        cellAlign: 'center',
        enableGrouping: false,
      },
      {
        accessorKey: 'amount',
        header: () => 'VALOR',
        cell: cellToMoney,
        enableGrouping: false,
      },
      {
        accessorKey: 'interest',
        header: () => 'JUROS',
        cell: cellToMoney,
        enableGrouping: false,
      },
      {
        accessorKey: 'principalValue',
        header: () => 'PRINCIPAL',
        cell: cellToMoney,
        enableGrouping: false,
      },
      {
        accessorKey: 'initialBalance',
        header: () => 'SALDO INICIAL',
        cell: cellToMoney,
        enableGrouping: false,
      },
      {
        accessorKey: 'finalBalance',
        header: () => 'SALDO FINAL',
        cell: cellToMoney,
        enableGrouping: false,
      },
      {
        accessorKey: 'iof',
        header: () => 'IOF',
        cell: cellToMoney,
        enableGrouping: false,
      },
      {
        accessorKey: 'status',
        header: () => 'STATUS',
        cell: renderStatus,
        enableGrouping: false,
      },
      {
        accessorKey: 'managedStatus',
        header: () => 'RESP. PARCELA',
        cell: renderManagedStatus,
        enableGrouping: false,
      },
      {
        id: 'details',
        header: () => 'AÇÕES',
        cellAlign: 'center',
        headerAlign: 'center',
        showPayOverdue,
        cell: cellToActions,
      },
    ];

    return initialColumns.filter((column) => !hiddenColumns?.includes(column.accessorKey || column.id));
  }, [hiddenColumns, document]);

  return (
    <Table
      noItemsMessage="Nenhuma parcela encontrada"
      loading={loading}
      data={data}
      responsiveCols={['ccb', 'installmentNumber', 'dueDate', 'amount', 'status', 'managedStatus', 'details']}
      grouping={['ccb']}
      columns={columns}
      isTHeadFixed
      maxHeight={600}
    />
  );
}
